import { ApiStatuses, IPagination, Nullable, SortTypes } from "../../app/types";
import { I18nString } from "../i18n";

export interface IProductsListFilters extends IPagination {
  searchString: string;
  categoriesFilter: string[];
  statusFilter: string;
  disabledSystemsFilter: boolean;
  nameFilterSort: SortTypes;
}

export interface IProductsPortfolioSliceState {
  list: IProduct[];
  filters: IProductsListFilters;
  status: ApiStatuses;
  selectedProduct: Nullable<IProduct>;
  categories: IProductCategory[];
  labels: IProductLabel[];
}

export interface IProductSku {
  id: number;
  createdOn: string;
  productSkuGroupId: number;
  sku: string;
  updatedOn: string;
  barcodes?: string[];
}

export interface IProductCategory {
  id: number;
  imageUrl: string;
  metadata: Record<string, string>;
  name: I18nString;
  parentId: Nullable<number>;
  updatedAt: string;
  createdAt: string;
}

export interface IProductLabel {
  id: string;
  name: I18nString;
  imageUrl: string;
  metadata: Record<string, string>;
  updatedAt: string;
  createdAt: string;
}

export interface IProductImage {
  createdAt: string;
  description: string
  id: string;
  metadata: Record<string, string>
  primary: boolean;
  updatedAt: string;
  url: string;
}

export interface IProductPriceDetail {
  price: number;
  sublines: { amount: number, description: string }[];
}

export interface IProductPrice {
  default: IProductPriceDetail | null;
}

export interface IProduct {
  barcodes: string[];
  brand: I18nString;
  bundleProductIds: string[];
  categoryId: Nullable<number>;
  createdAt: string;
  description: I18nString;
  externalId: Nullable<string>;
  heightMm: Nullable<number>;
  id: number;
  identifiers: IProductIdentifier[];
  images: IProductImage[];
  displaySize: EDisplaySize;
  price: IProductPrice;
  labelIds: string[];
  lengthMm: Nullable<number>;
  metadata: ProductMetadata;
  name: I18nString;
  pickingType: EPickingType;
  status: EProductStatus;
  temperature: EProductTemperature;
  type: EProductType;
  updatedAt: string;
  volumeL: Nullable<number>;
  weightKg: Nullable<number>;
  widthMm: Nullable<number>;
}

export interface IProductSummary {
  name: I18nString;
  primaryImageUrl: string;
  identifiers: IProductIdentifier[];
}

export enum EPickingType {
  Automated = "automated",
  Manual = "manual",
  External = "external",
  Integrated = "integrated",
}

export enum EDisplaySize {
  Default = "default",
  Medium = "medium",
  Small = "small",
  Tiny = "tiny",
}

export enum EProductTemperature {
  Frozen = "frozen",
  Chilled = "chilled",
  Ambient = "ambient"
}

export enum EProductStatus {
  Pending = "pending",
  Active = "active",
  Disabled = "disabled",
  Inactive = "inactive",
}

export enum EProductType {
  Regular = "regular",
  Bundle = "bundle",
}

export enum EProductIdentifierType {
  SKU = "SKU",
  EAN = "EAN",
  UPC = "UPC",
}

export interface IProductIdentifier {
  type: EProductIdentifierType;
  value: string;
}

export interface IProductModifier {
  key: string;
  name: I18nString;
  values: {
    value: string;
    name: I18nString;
  }[]
}

export interface IProductFormValues {
  name: string;
  brand: string;
  status: EProductStatus;
  description: string;
  type: EProductType;
  image: string;
  displaySize: EDisplaySize;
  imageDescription: string;
  temperature: EProductTemperature;
  pickingType: EPickingType;
  categoryName?: string;
  categoryId?: string;
  price: number;
  heightMm: number;
  weightKg: number;
  volumeL: number;
  lengthMm: number;
  widthMm: number;
  barcodes: string[];
}

export interface ICreateProduct {
  name: I18nString;
  brand?: I18nString;
  description?: I18nString;
  price?: {
    default: IProductPriceDetail;
  }
  image: string;
  displaySize: EDisplaySize;
  //type: EProductType;
  temperature: EProductTemperature;
  categoryId?: number;
  pickingType: EPickingType;
  heightMm?: number;
  weightKg?: number;
  volumeL?: number;
  lengthMm?: number;
  widthMm?: number;
  barcodes: string[];
}

export interface IUpdateProduct {
  name: I18nString;
  status: EProductStatus;
  brand: I18nString;
  description?: I18nString;
  image: string;
  displaySize: EDisplaySize;
  //type: EProductType;
  temperature: EProductTemperature;
  categoryId?: number;
  pickingType: EPickingType;
  price: IProductPrice;
  heightMm?: number;
  weightKg?: number;
  volumeL?: number;
  lengthMm?: number;
  widthMm?: number;
  barcodes: string[];
}

type PartialRecord<K extends keyof any, T> = {
  [P in K]?: T;
};

export type ProductMetadata = PartialRecord<string, string>;

export type IOrderWithMetadata = IProduct & ProductMetadata;